// labelForIssueLink — resolve an IssueLink's human-readable label from the
// register items it could point at. Shared by IssueLinkPicker (the selected
// chips) and IssueDetailDrawer (the "Linked to" strip) so the two surfaces
// render the same name for a link and can never drift. Falls back to the raw
// targetId (a ULID) when the target can't be resolved — the registers haven't
// loaded yet, or the target was deleted — so a link is always identifiable.

import type {
  Deliverable,
  IssueLink,
  Kpi,
  KpiMeasurement,
  Milestone,
} from "../../../../types";

export interface IssueLinkRegisters {
  deliverables: Deliverable[];
  milestones: Milestone[];
  kpis: Kpi[];
  measurements: KpiMeasurement[];
}

export const EMPTY_ISSUE_LINK_REGISTERS: IssueLinkRegisters = {
  deliverables: [],
  milestones: [],
  kpis: [],
  measurements: [],
};

export function labelForIssueLink(
  link: IssueLink,
  reg: IssueLinkRegisters,
): string {
  if (link.targetType === "deliverable") {
    return (
      reg.deliverables.find((d) => d.id === link.targetId)?.name ??
      link.targetId
    );
  }
  if (link.targetType === "milestone") {
    return (
      reg.milestones.find((m) => m.id === link.targetId)?.name ?? link.targetId
    );
  }
  if (link.targetType === "kpi") {
    const k = reg.kpis.find((x) => x.id === link.targetId);
    return k ? `${k.name} (KPI ${k.kpiNumber})` : link.targetId;
  }
  const m = reg.measurements.find((x) => x.id === link.targetId);
  return m ? `${m.name || "Period"} ${m.period}` : link.targetId;
}
